import {
  computed,
  nextTick,
  ref,
  toValue,
  watch,
  watchEffect,
  type MaybeRefOrGetter,
} from "vue";
import { useFieldsetValidationKit } from "../form/useFieldsetValidationKit";
import { US_STATES_BY_ABBREVIATION } from "./usStates";
import { useUSAddressFormData } from "./_useUSAddressFormData";
import { useUSAddressZip } from "./_useUSAddressZip";
import { useUSAddressValidation } from "./_useUSAddressValidation";

interface UseUSAddressOptions {
  fieldsetRef: MaybeRefOrGetter<HTMLFieldSetElement | null>;
  addressId: string;
  required?: MaybeRefOrGetter<boolean>;
}

export interface USAddressSubmitPayload {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Focuses the first invalid input within a container.
 * @param container - The container element to search within.
 */
function focusFirstInvalid(container: HTMLElement | null) {
  if (!container) return;
  const target = container.querySelector<HTMLElement>(
    '.k-invalid input, .k-invalid[tabindex="0"]',
  );
  target?.focus();
}

function focusoutLeavesFieldset(
  fieldsetRef: HTMLFieldSetElement | null,
  event: FocusEvent,
) {
  const fieldset = fieldsetRef ?? (event.currentTarget as HTMLElement | null);
  const next = event.relatedTarget as Node | null;

  return !fieldset || !fieldset.contains(next);
}

/**
 * Composable for managing a US address form.
 * @param options - The options object containing fieldset reference and address id.
 * @returns An object containing form state, validation state, and handlers.
 */
export function useUSAddress(options: UseUSAddressOptions) {
  const fieldsetRef = ref<HTMLFieldSetElement | null>(
    toValue(options.fieldsetRef),
  );

  watchEffect(() => {
    fieldsetRef.value = toValue(options.fieldsetRef);
  });

  const submitted = ref(false);
  const optionalFeedbackReviewed = ref(false);
  const isRequired = computed(() => toValue(options.required) ?? true);
  const {
    formData,
    onAddress1Input,
    onAddress2Input,
    onCityInput,
    onStateChange,
    resetFormData,
  } = useUSAddressFormData();

  const zipInputId = `${options.addressId}-zip`;

  const { zipDisplay, isZipValid, zipWrapperClass, onZipInput, resetZip } =
    useUSAddressZip(formData, zipInputId);

  const {
    strictAddress1Valid,
    strictCityValid,
    strictStateValid,
    strictZipValid,
    strictAddressValid,
  } = useUSAddressValidation({
    formData,
    isZipValid,
  });

  const validationKit = useFieldsetValidationKit(fieldsetRef, {
    submitted,
    isValid: () => strictAddressValid.value,
  });

  const { touched, fieldsetValidate, onFocusout, reset } = validationKit;

  const fieldValidations = validationKit.createFieldValidationsMap({
    address1: strictAddress1Valid,
    city: strictCityValid,
    state: strictStateValid,
    zip: strictZipValid,
    address: strictAddressValid,
  });

  const {
    address1: rawAddress1Valid,
    city: rawCityValid,
    state: rawStateValid,
    zip: rawZipValid,
    address: rawAddressValid,
  } = fieldValidations;

  const addressValidate = computed(() =>
    isRequired.value ? fieldsetValidate.value : false,
  );

  const address1Valid = computed(() =>
    isRequired.value ? rawAddress1Valid.value : true,
  );

  const cityValid = computed(() =>
    isRequired.value ? rawCityValid.value : true,
  );

  const stateValid = computed(() =>
    isRequired.value ? rawStateValid.value : true,
  );

  const zipValid = computed(() =>
    isRequired.value ? rawZipValid.value : true,
  );

  const addressValid = computed(() =>
    isRequired.value ? rawAddressValid.value : true,
  );

  const stateDefaultItem = { value: null, label: "State" };
  const stateOptions = US_STATES_BY_ABBREVIATION.map(({ label, value }) => ({
    label,
    value,
  }));

  const normalizedPayload = computed<USAddressSubmitPayload>(() => ({
    address1: formData.address1.trim(),
    address2: formData.address2.trim(),
    city: formData.city.trim(),
    state: (formData.state ?? "").trim().toUpperCase(),
    zip: formData.zip.replace(/\D/g, ""),
  }));

  const isAddressEmpty = computed(() => {
    return (
      formData.address1.trim() === "" &&
      formData.address2.trim() === "" &&
      formData.city.trim() === "" &&
      (formData.state ?? "").trim() === "" &&
      formData.zip.replace(/\D/g, "") === ""
    );
  });

  /**
   * Indicates whether any input has been provided in the optional address fields.
   * @returns True if any address field has input, false otherwise.
   */
  const optionalAddressHasAnyInput = computed(() => !isAddressEmpty.value);

  /**
   * Indicates whether any required fields are missing in the optional address section.
   * @returns True if any required field is missing, false otherwise.
   */
  const optionalMissingRequiredFields = computed(() => {
    return (
      !isRequired.value &&
      optionalAddressHasAnyInput.value &&
      (!strictAddress1Valid.value ||
        !strictCityValid.value ||
        !strictStateValid.value)
    );
  });

  /**
   * Indicates whether the optional zip code field is invalid.
   * @returns True if the zip code is invalid, false otherwise.
   */
  const optionalZipInvalid = computed(() => {
    const zipDigits = formData.zip.replace(/\D/g, "");

    return (
      !isRequired.value &&
      optionalAddressHasAnyInput.value &&
      zipDigits.length !== 5 &&
      zipDigits.length !== 9
    );
  });

  /**
   * Indicates whether any part of the optional address is invalid.
   * @returns True if any part of the optional address is invalid, false otherwise.
   */
  const optionalPartialInvalid = computed(() => {
    return (
      !isRequired.value &&
      optionalAddressHasAnyInput.value &&
      !strictAddressValid.value
    );
  });

  /**
   * Indicates whether optional address feedback should be active.
   * @returns True if feedback should be active, false otherwise.
   */
  const optionalFeedbackActive = computed(() => {
    return (
      !isRequired.value &&
      optionalPartialInvalid.value &&
      (optionalFeedbackReviewed.value || submitted.value)
    );
  });

  // Resets the optional feedback reviewed state when the normalized payload
  // changes and the address is not required.
  watch(normalizedPayload, () => {
    if (!isRequired.value) {
      optionalFeedbackReviewed.value = false;
    }
  });

  watch(isRequired, (required) => {
    if (!required) {
      submitted.value = false;
      optionalFeedbackReviewed.value = false;
      reset();
    }
  });

  /**
   * Validates address state for parent-owned submit flow.
   * Activates UI error visibility.
   * @returns An object containing the validity status and the normalized payload.
   */
  const validateForSubmit = async () => {
    if (!isRequired.value) {
      if (isAddressEmpty.value || strictAddressValid.value) {
        submitted.value = false;
        return {
          isValid: true as const,
          payload: normalizedPayload.value,
        };
      }

      submitted.value = true;
      return { isValid: false as const, payload: null };
    }

    submitted.value = true;

    if (!strictAddressValid.value) {
      await nextTick();
      focusFirstInvalid(fieldsetRef.value);
      return { isValid: false as const, payload: null };
    }

    return {
      isValid: true as const,
      payload: normalizedPayload.value,
    };
  };

  /**
   * Resets the US address form.
   * Deactivates UI error visibility.
   */
  const resetAddress = () => {
    submitted.value = false;
    optionalFeedbackReviewed.value = false;
    reset();
    resetFormData();
    resetZip();
  };

  const onFieldsetFocusout = (event: FocusEvent) => {
    const leftFieldset = focusoutLeavesFieldset(fieldsetRef.value, event);

    onFocusout(event);

    if (!isRequired.value && leftFieldset) {
      optionalFeedbackReviewed.value = true;
    }
  };

  return {
    formData,
    submitted,
    touched,
    addressValidate,
    address1Valid,
    cityValid,
    stateValid,
    zipValid,
    addressValid,
    optionalFeedbackActive,
    optionalPartialInvalid,
    optionalMissingRequiredFields,
    optionalZipInvalid,
    stateDefaultItem,
    stateOptions,
    zipDisplay,
    zipWrapperClass,
    onAddress1Input,
    onAddress2Input,
    onCityInput,
    onStateChange,
    onZipInput,
    onFieldsetFocusout,
    normalizedPayload,
    validateForSubmit,
    reset: resetAddress,
  };
}
